import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { useLazyQuery } from '@apollo/client';

import { GET_SEARCH_RESULTS } from '@atom/graph/search';
import { List } from '@atom/mui';
import { SearchInput, SearchResultsType } from '@atom/types/search';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AllResults from './AllResults';
import InventoryResultRow from './InventoryResultRow';
import SearchPortalHeader from './SearchPortalHeader';
import UserResultRow from './UserResultRow';
import WorkResultRow from './WorkResultRow';

import './searchPortal.css';

const style = {
  list: {
    padding: 0,
    margin: 0,
  },
};

export enum View {
  ALL = 'ALL',
  INVENTORY = 'INVENTORY',
  WORK = 'WORK',
  USERS = 'USERS',
}

const SearchPortal = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const [results, setResults] = useState<SearchResultsType>();
  const [activeView, setActiveView] = useState<View>(View.ALL);

  const [getSearch, { loading }] = useLazyQuery<
    { search: SearchResultsType },
    { input: SearchInput }
  >(GET_SEARCH_RESULTS, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      setResults(data?.search);
    },
  });

  useEffect(() => {
    getSearch({
      variables: {
        input: {
          query,
          top: false,
          showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
          includeAssets:
            activeView === View.INVENTORY || activeView === View.ALL,
          includeWorkOrders:
            activeView === View.WORK || activeView === View.ALL,
          includeUsers: activeView === View.USERS || activeView === View.ALL,
        },
      },
    });
  }, [query, activeView]);

  const content = useMemo(() => {
    const views = {
      [View.ALL]: <AllResults query={query} results={results} />,
      [View.INVENTORY]: results?.assets?.map(asset => (
        <InventoryResultRow query={query} asset={asset} />
      )),
      [View.WORK]: results?.workOrders?.map(work => (
        <WorkResultRow query={query} work={work} />
      )),
      [View.USERS]: results?.users?.map(user => (
        <UserResultRow query={query} user={user} />
      )),
    };

    return views[activeView];
  }, [loading, results, activeView]);

  const searchTotal = useMemo(() => {
    if (loading || isNilOrEmpty(results)) {
      return 0;
    }

    const { users, assets, workOrders } = results;

    switch (activeView) {
      case View.ALL: {
        return users.length + assets.length + workOrders.length;
      }
      case View.INVENTORY: {
        return assets.length;
      }
      case View.USERS: {
        return users.length;
      }
      case View.WORK: {
        return workOrders.length;
      }
      default: {
        return 0;
      }
    }
  }, [loading, results, activeView]);

  const searchTotalText =
    loading || isNilOrEmpty(results)
      ? 'Searching ...'
      : `${searchTotal} search results`;

  return (
    <div styleName="portal-container">
      <SearchPortalHeader
        query={query}
        activeView={activeView}
        setActiveView={setActiveView}
      />
      <div styleName="search-portal-results-container">
        <div styleName="search-portal-results">
          <div styleName="search-portal-results-heading">{searchTotalText}</div>
          <div styleName="content-container">
            <List style={style.list}>{content}</List>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPortal;
